import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckSquareOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Flex } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import BillingContext from '@totem/components/ticketing/billing/billingContext';
import { UnbilledTimeEntry } from '@totem/components/ticketing/ticketing';
import { BillingFilterOptions } from '@totem/components/ticketing/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { formatDateOnly, isNotNull, isNull } from '@totem/utilities/common';
import { downloadFile } from '@totem/utilities/fileUtilities';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

const UnbilledTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const {
    unbilledData,
    loading,
    selectedUnbilled,
    selectUnbilled,
    deselectUnbilled,
    selectAllUnbilled,
    clearSelectedUnbilled,
    selectedOrganizationId,
  } = useContext(BillingContext);

  const [filteredData, setFilteredData] = useState<UnbilledTimeEntry[]>([]);
  const [filterOptions, setFilterOptions] = useState<BillingFilterOptions>({
    resources: [],
    billingType: [],
    salesOrder: [],
    ticketNumber: [],
    organization: [],
    building: [],
    task: [],
  });
  const [input, updateInput] = useState<BillingFilterOptions>({
    resources: [],
    billingType: [],
    salesOrder: [],
    ticketNumber: [],
    organization: [],
    building: [],
    task: [],
  });

  useEffect(() => {
    if (isNotNull(unbilledData)) {
      const newFilterOptions: BillingFilterOptions = {
        resources: [...new Set(unbilledData.map((item) => item.resource.email))]
          .filter((item) => item !== '')
          .sort((compA, compB) => sortStringAscending(compA, compB)),
        billingType: [
          ...new Set(
            unbilledData.map((item) => item.ticket.billingMethodology),
          ),
        ]
          .filter((item) => item !== '')
          .sort((compA, compB) => sortStringAscending(compA, compB)),
        salesOrder: [
          ...new Set(unbilledData.map((item) => item.ticket.salesOrder)),
        ]
          .filter((item) => item !== '')
          .sort((compA, compB) => sortStringAscending(compA, compB)),
        ticketNumber: [
          ...new Set(unbilledData.map((item) => item.ticket.number)),
        ]
          .filter((item) => item !== '')
          .sort((compA, compB) => sortStringAscending(compA, compB)),
        organization: [
          ...new Set(unbilledData.map((item) => item.organization.name)),
        ]
          .filter((item) => item !== '')
          .sort((compA, compB) => sortStringAscending(compA, compB)),
        building: [...new Set(unbilledData.map((item) => item.building.name))]
          .filter((item) => item !== '')
          .sort((compA, compB) => sortStringAscending(compA, compB)),
        task: [...new Set(unbilledData.map((item) => item.task.name))]
          .filter((item) => item !== '')
          .sort((compA, compB) => sortStringAscending(compA, compB)),
      };
      setFilterOptions(newFilterOptions);
    }
  }, [unbilledData]);

  useEffect(() => {
    let newFilteredData = unbilledData;
    if (isNotNull(input.resources) && input.resources.length > 0) {
      newFilteredData = newFilteredData.filter((item) =>
        input.resources.includes(item.resource.email),
      );
    }
    if (isNotNull(input.billingType) && input.billingType.length > 0) {
      newFilteredData = newFilteredData.filter((item) =>
        input.billingType.includes(item.ticket.billingMethodology),
      );
    }
    if (isNotNull(input.salesOrder) && input.salesOrder.length > 0) {
      newFilteredData = newFilteredData.filter((item) =>
        input.salesOrder.includes(item.ticket.salesOrder),
      );
    }
    if (isNotNull(input.ticketNumber) && input.ticketNumber.length > 0) {
      newFilteredData = newFilteredData.filter((item) =>
        input.ticketNumber.includes(item.ticket.number),
      );
    }
    if (isNotNull(input.organization) && input.organization.length > 0) {
      newFilteredData = newFilteredData.filter((item) =>
        input.organization.includes(item.organization.name),
      );
    }
    if (isNotNull(input.building) && input.building.length > 0) {
      newFilteredData = newFilteredData.filter((item) =>
        input.building.includes(item.building.name),
      );
    }
    if (isNotNull(input.task) && input.task.length > 0) {
      newFilteredData = newFilteredData.filter((item) =>
        input.task.includes(item.task.name),
      );
    }

    setFilteredData(newFilteredData);
  }, [unbilledData, input]);

  const setInput = (updated: Partial<BillingFilterOptions>) => {
    updateInput({ ...input, ...updated });
  };

  const escapeCsvValue = (value: string | number) => {
    const str = String(value ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const handleExport = () => {
    const headers = [
      'Date',
      'Hours',
      'Resource',
      'Billing Type',
      'Sales Order',
      'Description',
      'Task',
      'Ticket Number',
      'Summary',
      'Organization',
      'Building',
    ];

    const rows = filteredData.map((item) => [
      escapeCsvValue(formatDateOnly(item.timeEntry.date)),
      escapeCsvValue(item.timeEntry.hoursLogged),
      escapeCsvValue(item.resource.name),
      escapeCsvValue(item.ticket.billingMethodology),
      escapeCsvValue(item.ticket.salesOrderLine),
      escapeCsvValue(item.timeEntry.description),
      escapeCsvValue(
        item.task?.name ? `${item.task.number}: ${item.task.name}` : '',
      ),
      escapeCsvValue(item.ticket.number),
      escapeCsvValue(item.ticket.summary),
      escapeCsvValue(item.organization.name),
      escapeCsvValue(item.building.name),
    ]);

    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join(
      '\n',
    );
    downloadFile('unbilled', csv);
  };

  const columns: ColumnProps<UnbilledTimeEntry>[] = [
    {
      title: (
        <Flex gap={8} align="center">
          <CheckSquareOutlined onClick={() => selectAllUnbilled()} />
          {selectedUnbilled.length > 0 && (
            <CloseCircleOutlined onClick={() => clearSelectedUnbilled()} />
          )}
        </Flex>
      ),
      dataIndex: 'id',
      key: 'id',
      render: (_, record: UnbilledTimeEntry) => (
        <>
          {permissions.billingAdministratorIBOrg.hasPermissions(userProfile) &&
            (selectedOrganizationId === '' ||
              selectedOrganizationId === record.organization.id) && (
              <Checkbox
                checked={
                  isNull(selectedUnbilled)
                    ? false
                    : selectedUnbilled.findIndex(
                        (chk) => chk.id === record.id,
                      ) >= 0
                }
                onChange={(evt) => {
                  if (evt.target.checked) {
                    selectUnbilled(record);
                  } else {
                    deselectUnbilled(record);
                  }
                }}
              />
            )}
        </>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'timeEntry.date',
      key: 'date',
      render: (_, record: UnbilledTimeEntry) =>
        `${formatDateOnly(record.timeEntry.date)}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.timeEntry.date, compB.timeEntry.date),
    },

    {
      title: 'Hours',
      dataIndex: 'timeEntry.hoursLogged',
      key: 'hoursLogged',
      align: 'right',
      render: (_, record: UnbilledTimeEntry) => (
        <Hours amount={record.timeEntry.hoursLogged} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.timeEntry.hoursLogged,
          compB.timeEntry.hoursLogged,
        ),
    },
    {
      title: 'Resource',
      dataIndex: 'resource.name',
      key: 'resources',
      render: (_, record: UnbilledTimeEntry) => `${record.resource.name}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.resource.name, compB.resource.name),
      filterMultiple: true,
      filteredValue: stringifyArray(input.resources),
      filters: filterOptions.resources.map((option) => {
        return {
          text: option,
          value: option,
        };
      }),
    },
    {
      title: 'Billing Type',
      dataIndex: 'ticket.billingMethodology',
      key: 'billingType',
      render: (_, record: UnbilledTimeEntry) =>
        `${record.ticket.billingMethodology}`,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.ticket.billingMethodology,
          compB.ticket.billingMethodology,
        ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.billingType),
      filters: filterOptions.billingType.map((option) => {
        return {
          text: option,
          value: option,
        };
      }),
    },
    {
      title: 'Sales Order',
      dataIndex: 'ticket.salesOrder',
      key: 'salesOrder',
      render: (_, record: UnbilledTimeEntry) => {
        if (record.ticket.salesOrderLine === '') {
          return <span />;
        }
        return <span>{record.ticket.salesOrderLine}</span>;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.ticket.salesOrderLine,
          compB.ticket.salesOrderLine,
        ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.salesOrder),
      filters: filterOptions.salesOrder.map((option) => {
        return {
          text: option,
          value: option,
        };
      }),
    },
    {
      title: 'Description',
      dataIndex: 'timeEntry.description',
      key: 'timeEntry.description',
      render: (_, record: UnbilledTimeEntry) => record.timeEntry.description,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.timeEntry.description,
          compB.timeEntry.description,
        ),
    },
    {
      title: 'Task',
      dataIndex: 'task.number',
      key: 'task',
      render: (_, record: UnbilledTimeEntry) => {
        if (record.task !== null && record.task.name !== '') {
          return `${record.task.number}: ${record.task.name}`;
        }
        return '';
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.task.number, compB.task.number),
      filterMultiple: true,
      filteredValue: stringifyArray(input.task),
      filters: filterOptions.task.map((option) => {
        return {
          text: option,
          value: option,
        };
      }),
    },

    {
      title: 'Ticket Number',
      dataIndex: 'ticket.number',
      key: 'ticketNumber',
      render: (_, record: UnbilledTimeEntry) => (
        <Link
          to={`/dashboard/ticket/${record.ticket.id}`}
          target={record.ticket.id}
        >
          {record.ticket.number}
        </Link>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.ticket.number, compB.ticket.number),
      filterMultiple: true,
      filteredValue: stringifyArray(input.ticketNumber),
      filters: filterOptions.ticketNumber.map((option) => {
        return {
          text: option,
          value: option,
        };
      }),
    },
    {
      title: 'Summary',
      dataIndex: 'ticket.summary',
      key: 'summary',
      render: (_, record: UnbilledTimeEntry) => record.ticket.summary,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.ticket.summary, compB.ticket.summary),
    },
    {
      title: 'Organization',
      dataIndex: 'organization.name',
      key: 'organization',
      render: (_, record: UnbilledTimeEntry) => record.organization.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.organization.name, compB.organization.name),
      filterMultiple: true,
      filteredValue: stringifyArray(input.organization),
      filters: filterOptions.organization.map((option) => {
        return {
          text: option,
          value: option,
        };
      }),
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      render: (_, record: UnbilledTimeEntry) => record.building.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.building.name, compB.building.name),
      filterMultiple: true,
      filteredValue: stringifyArray(input.building),
      filters: filterOptions.building.map((option) => {
        return {
          text: option,
          value: option,
        };
      }),
    },
  ];

  const handleTableChange = (
    _updatedPagination: TablePaginationConfig,
    filters: SorterResult<UnbilledTimeEntry>,
  ) => {
    const { ...params } = filters;

    // @ts-ignore
    setInput({
      ...input,
      ...params,
    });
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 8,
        }}
      >
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
      </div>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(filteredData) ? filteredData : []}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          showSizeChanger: true,
        }}
        rowKey={(record) => record.id}
      />
    </>
  );
};

export default UnbilledTable;
